import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { MapPin, Warehouse, Clock, WarningCircle } from "@phosphor-icons/react";
import PlacePicker from "@/components/PlacePicker";

const DAY_LABELS = ["L", "M", "M", "J", "V", "S", "D"];

export default function DepotSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/depot").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  const patch = (updates) => setCfg((c) => ({ ...c, ...updates }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: cfg.name,
        address: cfg.address,
        postal_code: cfg.postal_code,
        city: cfg.city,
        country: cfg.country,
        latitude: cfg.latitude,
        longitude: cfg.longitude,
        radius_m: Number(cfg.radius_m) || 100,
        enabled: !!cfg.enabled,
        alerts_enabled: !!cfg.alerts_enabled,
        work_start: cfg.work_start,
        work_end: cfg.work_end,
        work_days: cfg.work_days || [1, 2, 3, 4, 5],
        search_radius_km: Number(cfg.search_radius_km) || 300,
      };
      const r = await api.patch("/depot", body);
      setCfg(r.data);
      toast.success("Dépôt enregistré");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const onPickPlace = (p) => {
    patch({
      address: p.street || p.address || "",
      postal_code: p.postal_code || "",
      city: p.city || "",
      country: p.country || "France",
      latitude: p.latitude,
      longitude: p.longitude,
      name: cfg?.name || p.name || "Dépôt",
    });
    setSearch("");
  };

  const toggleDay = (dow) => {
    const cur = cfg.work_days || [1, 2, 3, 4, 5];
    const next = cur.includes(dow) ? cur.filter((d) => d !== dow) : [...cur, dow].sort();
    patch({ work_days: next });
  };

  // Cache the human-readable full address line
  const fullAddress = useMemo(
    () => [cfg?.address, cfg?.postal_code, cfg?.city].filter(Boolean).join(", "),
    [cfg?.address, cfg?.postal_code, cfg?.city]
  );

  // OpenStreetMap embed URL centered on depot with a small bbox
  const mapSrc = useMemo(() => {
    if (!cfg?.latitude || !cfg?.longitude) return null;
    const delta = 0.004;
    const bbox = [
      cfg.longitude - delta,
      cfg.latitude - delta,
      cfg.longitude + delta,
      cfg.latitude + delta,
    ].join("%2C");
    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${cfg.latitude}%2C${cfg.longitude}`;
  }, [cfg?.latitude, cfg?.longitude]);

  if (!cfg) return <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 text-xs text-white/40">Chargement…</div>;

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-4" data-testid="depot-settings-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#005CFF]/20 border border-[#005CFF]/30 flex items-center justify-center shrink-0">
          <Warehouse size={18} weight="duotone" className="text-[#7CB2FF]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Dépôt (siège)</div>
          <div className="text-[11px] text-white/50 leading-tight mt-0.5">
            Utilisé pour les alertes GPS d&apos;arrivée/départ dépôt et le calcul automatique des heures de journée.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] uppercase tracking-widest text-white/40">Actif</span>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => patch({ enabled: v })} data-testid="depot-enabled" />
        </div>
      </div>

      {/* Address search */}
      <div>
        <label className="text-[10px] uppercase tracking-widest text-white/40">Adresse</label>
        <PlacePicker
          value={search}
          onChange={setSearch}
          onPick={onPickPlace}
          placeholder="Rechercher une adresse…"
          testid="depot-place-picker"
        />
        {cfg.address && (
          <div className="mt-2 text-xs text-white/70 flex items-center gap-1.5 truncate">
            <MapPin size={12} className="text-[#7CB2FF] shrink-0" />
            <span className="truncate">{fullAddress}</span>
          </div>
        )}
      </div>

      {/* Map preview */}
      {mapSrc && (
        <div className="rounded-xl overflow-hidden border border-white/10 relative" data-testid="depot-map">
          <iframe title="Dépôt" src={mapSrc} className="w-full h-48 bg-black" loading="lazy" />
        </div>
      )}

      {/* Radius slider */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] uppercase tracking-widest text-white/40">Rayon geofence</label>
          <span className="text-xs font-mono tabular-nums text-white/70" data-testid="depot-radius-value">{cfg.radius_m || 100} m</span>
        </div>
        <input
          type="range"
          min={30}
          max={500}
          step={10}
          value={cfg.radius_m || 100}
          onChange={(e) => patch({ radius_m: Number(e.target.value) })}
          className="w-full accent-[#005CFF]"
          data-testid="depot-radius-slider"
        />
        <div className="flex justify-between text-[9px] text-white/30 mt-1">
          <span>30 m</span><span>100 m</span><span>250 m</span><span>500 m</span>
        </div>
      </div>

      {/* Search radius (address autocomplete cap) */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] uppercase tracking-widest text-white/40">Rayon recherche adresse</label>
          <span className="text-xs font-mono tabular-nums text-white/70" data-testid="depot-search-radius-value">{cfg.search_radius_km || 250} km</span>
        </div>
        <input
          type="range"
          min={50}
          max={800}
          step={10}
          value={cfg.search_radius_km || 300}
          onChange={(e) => patch({ search_radius_km: Number(e.target.value) })}
          className="w-full accent-[#005CFF]"
          data-testid="depot-search-radius-slider"
        />
        <div className="flex justify-between text-[9px] text-white/30 mt-1">
          <span>50 km · 1h</span><span>300 km · 3h30</span><span>500 km · 6h</span><span>800 km</span>
        </div>
      </div>

      {/* Working hours */}
      <div className="border-t border-white/5 pt-4 space-y-3">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-white/50" />
          <div className="text-xs font-semibold">Horaires normaux</div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-white/50">de</span>
          <input
            type="time"
            value={cfg.work_start || "07:00"}
            onChange={(e) => patch({ work_start: e.target.value })}
            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white/80"
            data-testid="depot-work-start"
          />
          <span className="text-white/50">à</span>
          <input
            type="time"
            value={cfg.work_end || "19:00"}
            onChange={(e) => patch({ work_end: e.target.value })}
            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white/80"
            data-testid="depot-work-end"
          />
        </div>
        <div className="flex gap-1">
          {DAY_LABELS.map((lab, i) => {
            const dow = i + 1;
            const active = (cfg.work_days || [1, 2, 3, 4, 5]).includes(dow);
            return (
              <button
                key={dow}
                onClick={() => toggleDay(dow)}
                className={`flex-1 h-8 rounded-lg text-xs font-semibold transition-colors ${
                  active ? "bg-[#005CFF] text-white" : "bg-white/5 text-white/40 border border-white/10"
                }`}
                data-testid={`depot-day-${dow}`}
              >
                {lab}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-white/5">
          <WarningCircle size={14} className="text-amber-400 shrink-0" />
          <div className="text-[11px] text-white/60 flex-1">Alerter admin si événement hors horaires</div>
          <Switch checked={!!cfg.alerts_enabled} onCheckedChange={(v) => patch({ alerts_enabled: v })} data-testid="depot-alerts-enabled" />
        </div>
      </div>

      <Button
        onClick={save}
        disabled={saving || !cfg.latitude}
        className="w-full h-11 bg-[#005CFF] hover:bg-[#0047CC] rounded-xl text-sm font-semibold"
        data-testid="depot-save-btn"
      >
        {saving ? "Enregistrement…" : "Enregistrer"}
      </Button>
    </div>
  );
}
